import { useMemo } from 'react'
import { useLocale } from '../i18n/LocaleProvider'

/**
 * Locale-aware number, currency and date formatting.
 *
 * Built on Intl rather than on date-fns locale bundles, because Intl already knows that
 * Finnish writes 14.7.2026, Hebrew writes ‎14.7.2026 right to left, Polish uses a space as
 * the thousands separator, and that the euro sign goes after the number in most of these
 * languages and before it in none of them except English.
 */
/** The property's clock. Orders, arrivals and serving hours are all local to Kerimaki. */
export const ESTATE_TIME_ZONE = 'Europe/Helsinki'

export function useFormatters() {
  const { locale } = useLocale()

  return useMemo(() => {
    const currency = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    })
    const currencyPrecise = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    })
    const dateShort = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' })
    const dateLong = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' })
    const dateWithWeekday = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    const monthYear = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
    const weekdayNarrow = new Intl.DateTimeFormat(locale, { weekday: 'narrow' })

    // Anything the owner acts on happens at the estate. A dinner ordered for 18:00 is 18:00 in
    // Kerimaki whether the owner reads it from the kitchen or from a phone in another country,
    // so these two are pinned to the property's clock rather than the browser's.
    const timeAtEstate = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: ESTATE_TIME_ZONE,
    })
    const dateTimeAtEstate = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: ESTATE_TIME_ZONE,
    })

    const toDate = (value: string | Date) =>
      value instanceof Date ? value : new Date(`${value}T00:00:00`)

    return {
      money: (value: number | null | undefined) => (value == null ? '' : currency.format(value)),
      moneyPrecise: (value: number | null | undefined) =>
        value == null ? '' : currencyPrecise.format(value),
      dateShort: (value: string | Date) => dateShort.format(toDate(value)),
      dateLong: (value: string | Date) => dateLong.format(toDate(value)),
      dateWithWeekday: (value: string | Date) => dateWithWeekday.format(toDate(value)),
      monthYear: (value: Date) => monthYear.format(value),
      weekdayNarrow: (value: Date) => weekdayNarrow.format(value),
      timeAtEstate: (value: string | Date) => timeAtEstate.format(new Date(value)),
      dateTimeAtEstate: (value: string | Date) => dateTimeAtEstate.format(new Date(value)),
    }
  }, [locale])
}
